<i18n>
{
	"en": {
		"header": "Incoterms",
		"noIncotermsFound": "No Incoterms...",
		"code": "Code",
		"description.no": "Description (NO)",
		"description.en": "Description (EN)",
		"deleteIncoterm": "Delete Incoterm",
		"newIncoterm": "New Incoterm",
		"failedToFetch": "Failed to fetch incoterms",
		"failedToDelete": "Failed to delete incoterm",
		"failedToUpdateCode": "Failed to update incoterm code",
		"failedToUpdateDescription": "Failed to update incoterm description",
		"updatedCode": "Successfully updated incoterm code",
		"updatedDescription": "Successfully updated incoterm description",
		"updatedIncoterm": "Incoterm Updated",
		"deletedIncoterms": "Deleted incoterm | Deleted incoterm | Deleted {count} incoterms",
		"sync": "Copy from R10",
		"confirmSyncFromR10": "This will replace all existing Incoterms. Are you sure you want to copy from R10?",
		"confirmLabel": "Copy",
		"syncedIncoterms": "Successfully copied incoterms from R10",
		"failedToSync": "Failed to copy incoterms from R10",
		"getCustomerFrom": "Customer From",
		"getAddressFrom": "Address From",
		"sender": "Sender",
		"receiver": "Receiver"
	},
	"no": {
		"header": "Incoterms",
		"noIncotermsFound": "Ingen incoterms...",
		"code": "Kode",
		"description.no": "Beskrivelse (NO)",
		"description.en": "Beskrivelse (EN)",
		"deleteIncoterm": "Fjern Incoterm",
		"newIncoterm": "Legg til incoterm",
		"failedToFetch": "Kunne ikke hente incoterms",
		"failedToUpdateCode": "Kunne ikke oppdatere incoterm kode",
		"failedToUpdateDescription": "Kunne ikke oppdatere incoterm beskrivelse",
		"updatedCode": "Incoterm kode ble oppdatert",
		"updatedDescription": "Incoterm beskrivelse ble oppdatert",
		"updatedIncoterm": "Incoterm oppdatert",
		"deletedIncoterms": "Incoterm ble slettet | Incoterm ble slettet | Slettet {count} incoterms",
		"sync": "Kopier fra R10",
		"confirmSyncFromR10": "Dette vil erstatte alle eksisterende fraktbetingelser. Er du sikker på at du ønsker å kopiere fra R10?",
		"confirmLabel": "Kopier",
		"syncedIncoterms": "Incoterms ble kopiert fra R10",
		"failedToSync": "Kunne ikke kopiere incoterms fra R10",
		"getCustomerFrom": "Kunde fra",
		"getAddressFrom": "Adresse fra",
		"sender": "Avsender",
		"receiver": "Mottaker"
	}
}
</i18n>

<template>
	<t-vertical-panel class="incoterms">
		<div slot="header" class="incoterms-header">
			<t-panel-header size="md" icon="book" type="bp">
				{{ $t('header') }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="incoterms-sub-header">
			<t-button
				iconBefore="refresh"
				appearance="primary"
				width="150px"
				@click="onSyncIncoterms"
			>{{$t('sync')}}</t-button>
			<t-button
				:disabled="rowsSelected == 0"
				iconBefore="trash"
				appearance="primary"
				intent="danger"
				width="150px"
				type="bp"
				@click="onDeleteIncoterms"
			>{{$tc('deleteIncoterm', rowsSelected)}}</t-button>
			<t-button
				appearance="primary"
				width="150px"
				@click="onAddIncoterm"
			>{{$t('newIncoterm')}}</t-button>
		</div>
		<div class="incoterms-content">
			<t-grid
				v-if="incoterms"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinitions"
				:rowHeight="40"
				:items="incoterms"
				:selectableMode="['checkbox']"
				localStorageId="incotermsList"
				selectable
				@selected="onRowsSelected"
			>
				<template #code="{ item }">
					<t-text-input
						:value="item.code"
						:isInvalid="errors.code && item.id === errors.id"
						@change="updateCode( item, $event )"
					/>
				</template>
				<template #description.no="{ item }">
					<t-text-input
						:value="item.description.no"
						:isInvalid="errors.description && item.id === errors.id"
						@change="updateDescription( item, $event, 'no' )"
					/>
				</template>
				<template #description.en="{ item }">
					<t-text-input
						:value="item.description.en"
						:isInvalid="errors.description && item.id === errors.id"
						@change="updateDescription( item, $event, 'en' )"
					/>
				</template>
				<template #getCustomerFrom="{ item }">
					<div style="display: flex; flex-direction: row; ">
						<t-checkbox
							style="margin-right: 15px"
							:checked="item.getCustomerFrom === 'S'"
							:label="$t('sender')"
							name="getCustomerFrom"
							@change="updateGetCustomerFrom( item, 'S', $event )"
						/>
						<t-checkbox
							:checked="item.getCustomerFrom === 'M'"
							:label="$t('receiver')"
							name="getCustomerFrom"
							@change="updateGetCustomerFrom( item, 'M', $event )"
						/>
					</div>
				</template>
				<template #getAddressFrom="{ item }">
					<div style="display: flex; flex-direction: row; ">
						<t-checkbox
							style="margin-right: 15px"
							:checked="item.getAddressFrom === 'S'"
							:label="$t('sender')"
							name="getAddressFrom"
							@change="updateGetAddressFrom( item, 'S', $event )"
						/>
						<t-checkbox
							:checked="item.getAddressFrom === 'M'"
							:label="$t('receiver')"
							name="getAddressFrom"
							@change="updateGetAddressFrom( item, 'M', $event )"
						/>
					</div>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('noIncotermsFound')"
				icon="book"
				iconType="bp"
			/>
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<t-dialog
			:isShown="confirmSyncShown"
			:hasHeader="false"
			:confirmLabel="$t('confirmLabel')"
			intent="danger"
			icon="cog"
			iconType="bp"
			width="480px"
			@ok="onConfirmSync"
			@close="confirmSyncShown = false"
		>
			{{$tc('confirmSyncFromR10')}}
		</t-dialog>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/incoterms.store.js';

export default {
	name: 'Incoterms',
	data() {
		return {
			defaultColumnDefinitions: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			confirmSyncShown: false
		};
	},
	computed: {
		...mapState([
			'incoterms',
			'loading',
			'errors'
		]),
		columnDefinitions() {
			return [
				{
					field: 'code',
					displayName: this.$t('code'),
					width: 60,
					align: 'center'
				},
				{
					field: 'description.no',
					displayName: this.$t('description.no'),
					minWidth: 100
				},
				{
					field: 'description.en',
					displayName: this.$t('description.en'),
					minWidth: 100
				},
				{
					field: 'getCustomerFrom',
					displayName: this.$t('getCustomerFrom')
				},
				{
					field: 'getAddressFrom',
					displayName: this.$t('getAddressFrom')
				}
			];
		}
	},
	async mounted() {
		try {
			await this.getIncoterms();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods:{
		...mapActions([
			'getIncoterms',
			'setIncoterm',
			'deleteIncoterms',
			'addIncoterm',
			'syncIncoterms'
		]),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async updateCode(incoterm, data ) {
			try {
				await this.setIncoterm({ incoterm: incoterm, data: data, prop: 'code' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedIncoterm'),
					text: incoterm.code
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateCode'),
					text: incoterm.code
				});
			}
		},
		async updateDescription(incoterm, data, language) {
			try {
				await this.setIncoterm({ incoterm: incoterm, data: data, prop: `description.${language}` });
				this.$notify({
					type: 'success',
					title: this.$t('updatedIncoterm'),
					text: incoterm.code
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateDescription'),
					text: incoterm.code
				});
			}
		},
		async updateGetCustomerFrom(incoterm, senderOrReceiver, value) {
			try {
				await this.setIncoterm({ incoterm, data: value ? senderOrReceiver : undefined, prop: 'getCustomerFrom' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedIncoterm'),
					text: incoterm.code
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateGetCustomerFrom'),
					text: incoterm.code
				});
			}
		},
		async updateGetAddressFrom(incoterm, senderOrReceiver, value) {
			try {
				await this.setIncoterm({ incoterm, data: value ? senderOrReceiver : undefined, prop: 'getAddressFrom' });
				this.$notify({
					type: 'success',
					title: this.$t('updatedIncoterm'),
					text: incoterm.code
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdateDescription'),
					text: incoterm.code
				});
			}
		},
		async onDeleteIncoterms() {
			let selectedIncoterms = this.$refs.grid.getSelectedItems();
			if (!selectedIncoterms) return;
			try {
				await this.deleteIncoterms(selectedIncoterms);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedIncoterms', selectedIncoterms.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddIncoterm() {
			try {
				await this.addIncoterm();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToAdd')
				});
			}
		},
		onSyncIncoterms() {
			this.confirmSyncShown = true;
		},
		async onConfirmSync() {
			try {
				await this.syncIncoterms();
				this.$notify({
					type: 'success',
					title: this.$tc('syncedIncoterms')
				});
				await this.getIncoterms({ force: true });
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToSync')
				});
			}
		}
	}
};

</script>

<style lang="scss" scoped>
.incoterms {
	.incoterms-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.incoterms-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.incoterms-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
.list-item {
	display: flex;
	align-items: center;
	justify-items: flex-start;

	> div {
		margin-left: 10px;
	}
}
</style>
